import { zodResolver } from '@hookform/resolvers/zod';
import { useLocale } from '@solinkcorp/locale';
import { useEffect, useMemo, useRef } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { z } from 'zod';
import { DAY_INDICES } from '../day-part/constants';
import { type DayIndex, type DayParts, type ServiceDisplayDayPart } from '../day-part/types';

const TIME_REGEX = /^([01]\d|2[0-3]):[0-5]\d$/;

const dayEntrySchema = z
  .object({
    active: z.boolean(),
    start: z.string().regex(TIME_REGEX, 'Invalid time format (HH:MM)'),
    end: z.string().regex(TIME_REGEX, 'Invalid time format (HH:MM)'),
  })
  .refine((data) => !data.active || data.end > data.start, {
    message: 'Invalid time range',
    path: ['end'],
  });

export function createDayPartFormSchema(
  existingLabels: string[],
  locale1: string,
  locale2: string,
) {
  return z.object({
    label: z
      .string()
      .min(1, locale1)
      .refine((name) => !existingLabels.includes(name.trim().toLowerCase()), {
        message: locale2,
      }),
    days: z.record(z.enum(DAY_INDICES as [string, ...string[]]), dayEntrySchema),
  });
}

export type DayPartFormData = z.infer<ReturnType<typeof createDayPartFormSchema>>;
export type DayEntryFormData = z.infer<typeof dayEntrySchema>;

export const DEFAULT_TIME_START = '00:00';
export const DEFAULT_TIME_END = '01:00';

/** Convert API dayParts record → form days record */
function apiToFormDays(dayParts?: DayParts): Record<DayIndex, DayEntryFormData> {
  const days = {} as Record<DayIndex, DayEntryFormData>;
  const isNew = !dayParts;
  for (const idx of DAY_INDICES) {
    days[idx] = { active: isNew, start: DEFAULT_TIME_START, end: DEFAULT_TIME_END };
  }
  if (isNew) return days;

  for (const idx of DAY_INDICES) {
    const ranges = dayParts[idx];
    if (ranges && ranges.length > 0) {
      days[idx] = { active: true, start: ranges[0].start, end: ranges[0].end };
    }
  }
  return days;
}

/** Convert form days record → API dayParts record (sparse: only active days) */
export function formDaysToApi(days: Record<string, DayEntryFormData>): DayParts {
  const result: DayParts = {};
  for (const [day, entry] of Object.entries(days)) {
    if (entry.active) {
      result[day as DayIndex] = [{ start: entry.start, end: entry.end }];
    }
  }
  return result;
}

export function useDayPartForm(
  existingDayParts: ServiceDisplayDayPart[],
  dayPart?: ServiceDisplayDayPart,
  open?: boolean,
) {
  const locale = useLocale();
  const formLocale = locale.serviceDisplayMonitor.dayPartDialog;
  const existingLabels = useMemo(
    () =>
      existingDayParts
        .filter((dp) => dp.id !== dayPart?.id)
        .map((dp) => dp.label.trim().toLowerCase()),
    [existingDayParts, dayPart],
  );

  const schema = useMemo(
    () =>
      createDayPartFormSchema(
        existingLabels,
        formLocale.dayPartNameRequired,
        formLocale.dayPartNameAlreadyExists,
      ),
    [existingLabels, formLocale],
  );

  const schemaRef = useRef(schema);
  schemaRef.current = schema;
  const resolver: Resolver<DayPartFormData> = useMemo(
    () => (values, context, options) => zodResolver(schemaRef.current)(values, context, options),
    [],
  );

  const defaultValues = useMemo<DayPartFormData>(
    () => ({
      label: dayPart?.label ?? '',
      days: apiToFormDays(dayPart?.dayParts),
    }),
    [dayPart],
  );

  const form = useForm<DayPartFormData>({
    resolver,
    defaultValues,
  });

  useEffect(() => {
    form.reset(defaultValues);
  }, [open, defaultValues, form]);

  return form;
}
